import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Progress,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  OktonAlert,
  OktonEmptyState,
  OktonLoadingState,
} from "@okton/platform-ui";
import {
  AiConnection,
  AiInventoryJob,
  AiSource,
  AiSourcesState,
} from "./types";
import { inventoryRequest, loadInventory } from "./aiInventoryApi";

interface Props {
  systemId: string;
  accessToken: string;
  sources: AiSourcesState | null;
  connections: AiConnection[];
  dirty: boolean;
  busy: boolean;
}
const active = (job: AiInventoryJob): boolean =>
  ["queued", "running", "cancelling"].includes(job.state);
const labels: Record<AiInventoryJob["state"], string> = {
  queued: "Na fila",
  running: "Processando",
  cancelling: "Cancelando",
  completed: "Inventário concluído",
  cancelled: "Cancelado",
  failed: "Falhou",
};
const errors: Record<string, string> = {
  source_changed:
    "A fonte ou a autorização mudou. Revise as fontes antes de tentar novamente.",
  worker_expired: "A execução foi interrompida. Inicie um novo inventário.",
  transport_failed: "Não foi possível obter os arquivos da fonte.",
  reader_failed: "A leitura não foi concluída.",
  worker_failed: "A execução não foi concluída.",
};
function requestId(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("");
  return (
    hex.slice(0, 8) +
    "-" +
    hex.slice(8, 12) +
    "-" +
    hex.slice(12, 16) +
    "-" +
    hex.slice(16, 20) +
    "-" +
    hex.slice(20)
  );
}

const InventoryPanel: React.FC<Props> = ({
  systemId,
  accessToken,
  sources,
  connections,
  dirty,
  busy,
}) => {
  const [jobs, setJobs] = React.useState<AiInventoryJob[]>([]);
  const [error, setError] = React.useState("");
  const [pollError, setPollError] = React.useState("");
  const knownActive = React.useRef(false);
  const [loading, setLoading] = React.useState(true);
  const [pending, setPending] = React.useState(false);
  const [refresh, setRefresh] = React.useState(0);
  const token = React.useRef(accessToken);
  token.current = accessToken;
  const controller = React.useRef<AbortController | null>(null);
  const locked = React.useRef(false);
  const revision = React.useRef(0);
  const requestIds = React.useRef<Record<string, string>>({});
  const hasSession = Boolean(accessToken);

  React.useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let failures = 0;
    const schedule = (delay = 5000) => {
      if (!current.signal.aborted && !document.hidden)
        timer = setTimeout(read, delay);
    };
    async function read(): Promise<void> {
      if (inFlight || current.signal.aborted || document.hidden) return;
      inFlight = true;
      const version = revision.current;
      try {
        const data = await loadInventory(
          systemId,
          token.current,
          current.signal,
        );
        if (!Array.isArray(data))
          throw new Error("Resposta de inventário inválida.");
        if (!current.signal.aborted && version === revision.current) {
          setJobs(data);
          setPollError("");
          failures = 0;
          knownActive.current = data.some(active);
          if (knownActive.current) schedule();
        }
      } catch (failure) {
        if (!current.signal.aborted) {
          setPollError(
            failure instanceof Error
              ? failure.message
              : "Erro ao carregar inventário.",
          );
          failures += 1;
          if (knownActive.current)
            schedule(
              Math.min(30000, 5000 * Math.pow(2, Math.min(failures - 1, 3))),
            );
        }
      } finally {
        inFlight = false;
        if (!current.signal.aborted) setLoading(false);
      }
    }
    const visible = () => {
      if (timer) clearTimeout(timer);
      if (!document.hidden) void read();
    };
    if (hasSession) void read();
    else {
      setJobs([]);
      setLoading(false);
      setError("Sessão indisponível.");
    }
    document.addEventListener("visibilitychange", visible);
    return () => {
      current.abort();
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [systemId, hasSession, refresh]);

  async function mutate(
    suffix: string,
    body: object,
    key?: string,
  ): Promise<void> {
    const current = controller.current;
    if (!current || current.signal.aborted || locked.current || !hasSession)
      return;
    locked.current = true;
    setPending(true);
    setError("");
    revision.current += 1;
    try {
      const job = await inventoryRequest<AiInventoryJob>(
        systemId,
        token.current,
        current.signal,
        suffix,
        body,
      );
      if (!current.signal.aborted) {
        knownActive.current =
          active(job) ||
          jobs.some((item) => item.id !== job.id && active(item));
        setJobs((existing) => [
          job,
          ...existing.filter((item) => item.id !== job.id),
        ]);
        if (key) delete requestIds.current[key];
        setRefresh((value) => value + 1);
      }
    } catch (failure) {
      if (!current.signal.aborted)
        setError(
          failure instanceof Error
            ? failure.message
            : "Não foi possível processar o pedido.",
        );
    } finally {
      if (controller.current === current) {
        locked.current = false;
        setPending(false);
      }
    }
  }
  function start(source: AiSource): void {
    if (!source.id || !sources || dirty || busy || pending) return;
    const key = source.id + ":" + sources.revision;
    requestIds.current[key] = requestIds.current[key] || requestId();
    void mutate(
      "",
      {
        sourceId: source.id,
        revision: sources.revision,
        requestId: requestIds.current[key],
      },
      key,
    );
  }

  return (
    <Box mt="5" borderTopWidth="1px" pt="4">
      <Flex align="center" justify="space-between" mb="2">
        <Heading size="sm">Inventário dos arquivos</Heading>
        <Button
          size="xs"
          variant="ghost"
          isDisabled={pending || loading || !hasSession}
          onClick={() => {
            setError("");
            setPollError("");
            setRefresh((value) => value + 1);
          }}
        >
          Atualizar
        </Button>
      </Flex>
      <Text fontSize="sm" color="gray.500" mb="3">
        Confira os arquivos disponíveis para o mapeamento. Concluir o inventário
        ainda não gera a documentação por IA.
      </Text>
      {(error || pollError) && (
        <OktonAlert status="error" mb="3">
          <Text>{error || pollError}</Text>
        </OktonAlert>
      )}
      {dirty && (
        <Text fontSize="sm" mb="2">
          Salve as fontes antes de iniciar o inventário.
        </Text>
      )}
      <Flex wrap="wrap" gap="2" mb="4">
        {(sources?.items || []).map((source, index) => {
          const available = connections.some(
            (item) =>
              item.id === source.connectionId && item.inventoryEnabled === true,
          );
          const running = jobs.some(
            (item) => item.sourceId === source.id && active(item),
          );
          return (
            <Box key={source.id || index}>
              <Button
                size="sm"
                isDisabled={
                  !source.id ||
                  !source.enabled ||
                  source.kind !== "git" ||
                  !available ||
                  running ||
                  dirty ||
                  busy ||
                  pending ||
                  loading ||
                  !hasSession
                }
                onClick={() => start(source)}
              >
                Inventariar {source.label || "fonte"}
              </Button>
              {!available && (
                <Text fontSize="xs" color="gray.500">
                  Execução ainda não habilitada nesta conexão.
                </Text>
              )}
            </Box>
          );
        })}
      </Flex>
      {loading ? (
        <OktonLoadingState title="Carregando histórico..." minH="100px" />
      ) : jobs.length === 0 ? (
        <OktonEmptyState
          title="Nenhum inventário solicitado"
          description="Os inventários solicitados para as fontes deste sistema aparecerão aqui."
        />
      ) : null}
      <Stack spacing="3" aria-live="polite">
        {jobs.map((job) => (
          <Box key={job.id} borderWidth="1px" rounded="md" p="3">
            <Flex justify="space-between" align="center" wrap="wrap" gap="2">
              <Text fontSize="sm" fontWeight="semibold">
                {job.sourceLabel}
              </Text>
              <Badge
                colorScheme={
                  job.state === "completed"
                    ? "green"
                    : job.state === "failed"
                      ? "red"
                      : "gray"
                }
              >
                {labels[job.state]}
              </Badge>
              {["queued", "running"].includes(job.state) && (
                <Button
                  size="xs"
                  variant="outline"
                  isDisabled={pending}
                  onClick={() =>
                    void mutate(
                      "/" + encodeURIComponent(job.id) + "/cancel",
                      {},
                    )
                  }
                >
                  Cancelar
                </Button>
              )}
            </Flex>
            <Text fontSize="xs" color="gray.500">
              Solicitado em {new Date(job.createdAt).toLocaleString("pt-BR")} ·
              Revisão {job.revision}
            </Text>
            {job.state === "running" && job.phase === "downloading" && (
              <Text fontSize="sm">Obtendo os arquivos da fonte...</Text>
            )}
            {typeof job.progress.total === "number" &&
              job.progress.total > 0 && (
                <>
                  <Progress
                    mt="2"
                    size="xs"
                    value={Math.min(
                      100,
                      ((job.progress.processed || 0) / job.progress.total) *
                        100,
                    )}
                  />
                  <Text fontSize="sm">
                    {job.progress.processed || 0} de {job.progress.total}{" "}
                    entradas verificadas · {job.progress.accepted || 0} arquivos
                    aprovados
                  </Text>
                  {active(job) &&
                    job.progress.estimatedSecondsRemaining != null && (
                      <Text fontSize="xs" color="gray.500">
                        Previsão restante: cerca de{" "}
                        {Math.ceil(job.progress.estimatedSecondsRemaining)}{" "}
                        segundos.
                      </Text>
                    )}
                </>
              )}
            {job.result && (
              <Text fontSize="xs" color="gray.500">
                Versão da fonte: {job.result.commit.slice(0, 12)}
              </Text>
            )}
            {job.errorCode && job.state === "failed" && (
              <Text fontSize="sm" color="red.600">
                {errors[job.errorCode] ||
                  "Não foi possível concluir o inventário."}
              </Text>
            )}
          </Box>
        ))}
      </Stack>
      <Flex mt="5" align="center" wrap="wrap" gap="3">
        <Button size="sm" isDisabled>
          Mapear sistema
        </Button>
        <Text fontSize="sm" color="gray.500">
          Mapeamento ainda não configurado.
        </Text>
      </Flex>
    </Box>
  );
};
export const AiInventoryPanel: React.FC<Props> = (props) => (
  <InventoryPanel key={props.systemId} {...props} />
);
